import { APIRoutes } from "@/api";
import { NotificationType } from "@/config/notification";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";
import { userLastReadNotificationsSchema } from "@/services/users/schema";
import {
  QueryFunctionContext,
  UseMutationOptions,
  useMutation as useMutationTanstack,
  useQuery as useQueryTanstack,
} from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { z } from "zod";

async function getHasUnreadNotifications({ signal }: QueryFunctionContext) {
  const data = await fetch(APIRoutes.USER_NOTIFICATIONS_UNREAD, {
    cache: "no-cache",
    signal,
  }).then((res) => res.json());

  const response = ApiService.parseApiResponse(
    data,
    userLastReadNotificationsSchema,
  );

  if (response.status !== "success") {
    return null;
  }

  return response.data;
}

async function markNotificationsAsRead(
  variables: MarkNotificationsAsReadMutationVariables,
) {
  const params = new URLSearchParams();
  if (variables) {
    params.set("type", variables.types.join(","));
  }

  const url = `${APIRoutes.USER_NOTIFICATIONS_UNREAD}?${params.toString()}`;
  const data = await fetch(url, {
    method: "PATCH",
    body: JSON.stringify(variables),
  }).then((res) => res.json());

  const result = ApiService.parseApiResponse(data, z.any());

  return { success: result.status === "success" };
}

type MarkNotificationsAsReadMutationData = {
  readonly success: boolean;
};
type MarkNotificationsAsReadMutationError = unknown;
type MarkNotificationsAsReadMutationVariables = {
  types: NotificationType[];
} | void;
type MarkNotificationsAsReadMutationContext = {};

export namespace HasUnreadNotificationsQuery {
  export const keys = {
    all: ["user"],
    hasUnread: (userId: string) => [...keys.all, userId, "has-unread"] as const,
  } as const;

  export function invalidate(userId: string) {
    return queryClient.invalidateQueries({ queryKey: keys.hasUnread(userId) });
  }

  export function getData(userId: string) {
    return queryClient.getQueryData(keys.hasUnread(userId));
  }

  export function useQuery() {
    const { data: session } = useSession();

    return useQueryTanstack({
      queryKey: keys.hasUnread(session?.user?.id ?? ""),
      queryFn: getHasUnreadNotifications,
      enabled: !!session?.user?.id,
      gcTime: 0,
      staleTime: 0,
    });
  }

  export function useMutation() {
    const { data: session } = useSession();

    return useMutationTanstack<
      MarkNotificationsAsReadMutationData,
      MarkNotificationsAsReadMutationError,
      MarkNotificationsAsReadMutationVariables,
      MarkNotificationsAsReadMutationContext
    >({
      mutationKey: keys.hasUnread(session?.user.id ?? ""),
      mutationFn: markNotificationsAsRead,

      onSuccess: () => {
        // Invalidate the query to refetch the data
        invalidate(session?.user.id ?? "");
      },
    } as UseMutationOptions<
      MarkNotificationsAsReadMutationData,
      MarkNotificationsAsReadMutationError,
      MarkNotificationsAsReadMutationVariables,
      MarkNotificationsAsReadMutationContext
    >);
  }
}
